import { DataTable, Mono, OrgAvatar, StatusBadge, Text, type DataTableColumn } from "@athena/ui";
import { STATUS_LABEL, STATUS_VARIANT } from "@/types/Status";
import type { OrganizationStatus } from "@athena/common/graphql/generated/schema";

/**
 * The organizations table, as columns rather than a fixed table — same arrangement as `UserTable`.
 *
 * Two surfaces list organizations and they differ: the platform list shows contact email, member
 * count, status and whether chat is on, and nests sub-orgs under an expander; an organization's own
 * sub-orgs tab shows a flat list with its own actions. The identity column carries the indentation
 * and the expander, so nesting is a property of the row rather than of a separate component.
 */

export interface OrgRow {
    id: string;
    name: string;
    slug?: string | null;
    contactEmailAddress?: string | null;
    memberCount?: number;
    /** Members seen on the mobile app. Rendered against `memberCount`, never on its own. */
    appUserCount?: number;
    status?: OrganizationStatus;
    chatEnabled?: boolean | null;
    /**
     * Second line under the name, where the slug is not the useful thing — a sub-org list shows when
     * it was added. Falls back to the slug.
     */
    subtitle?: React.ReactNode;
    /** Indentation depth in a nested list. Zero, or absent, for a flat one. */
    depth?: number;
    /**
     * Whether this row has sub-organizations to expand into. The page decides — it comes from the
     * row's own `subOrganizations`, not from what has been lazily fetched so far, which is the
     * difference between a collapsed row and a childless one.
     */
    hasChildren?: boolean;
}

/**
 * Up to two letters, skipping connector words.
 *
 * Naively taking the first two words gave "College of Sciences" and "College of Fine Arts" both "CO" —
 * two sibling organizations with identical avatars, which is the opposite of what an avatar is for.
 * Dropping lowercase connectors makes them CS and CF.
 */
const CONNECTORS = new Set([
    "of",
    "the",
    "and",
    "for",
    "at",
    "in",
    "&",
]);

const initialsOf = (name: string): string => {
    const words = name
        .trim()
        .split(/\s+/)
        .filter((word) => word && !CONNECTORS.has(word.toLowerCase()));
    // Everything was a connector — fall back to the raw words rather than rendering nothing.
    const source = words.length > 0 ? words : name.trim().split(/\s+/);
    return (
        source
            .slice(0, 2)
            .map((word) => word[0] ?? "")
            .join("")
            .toUpperCase() || "?"
    );
};

const dash = (
    <Text muted className="text-sm">
        —
    </Text>
);

export const orgColumns = {
    /**
     * Name, slug and — in a nested list — the indent and expander. `expander` is supplied by the page
     * because only it knows what is loaded and what is still being fetched.
     */
    identity: (expander?: (org: OrgRow) => React.ReactNode) =>
        ({
            key: "identity",
            header: "Organization",
            cell: (org: OrgRow) => (
                <div className="flex items-center gap-3" style={{ paddingLeft: (org.depth ?? 0) * 24 }}>
                    {expander?.(org)}
                    {/* `fallback` is the glyph inside the avatar, not a label — passing the whole
                        name rendered it twice per row, once in the circle and once beside it. */}
                    <OrgAvatar id={org.id} fallback={initialsOf(org.name)} size="sm" className="shrink-0" />
                    <div className="min-w-0">
                        <Text size="sm" weight="semibold" className="truncate">
                            {org.name}
                        </Text>
                        {org.subtitle ? (
                            <Mono size="xs" muted className="truncate">
                                {org.subtitle}
                            </Mono>
                        ) : (
                            org.slug && (
                                <Mono size="xs" muted className="truncate">
                                    {org.slug}
                                </Mono>
                            )
                        )}
                    </div>
                </div>
            ),
        }) satisfies DataTableColumn<OrgRow>,

    contactEmail: {
        key: "contactEmail",
        header: "Contact email",
        cell: (org: OrgRow) => (org.contactEmailAddress ? <Mono size="xs">{org.contactEmailAddress}</Mono> : dash),
    } satisfies DataTableColumn<OrgRow>,

    members: {
        key: "members",
        header: "Users",
        cell: (org: OrgRow) => <Text size="sm">{(org.memberCount ?? 0).toLocaleString()}</Text>,
    } satisfies DataTableColumn<OrgRow>,

    /**
     * App adoption, as a count and a share of the roster.
     *
     * The percentage is the point. Rosters here run from a handful to 8,635, so "1,204" and "412"
     * next to each other say nothing about which organization has actually taken up the app — 14%
     * and 53% do. An org with no members shows a dash rather than dividing by zero.
     */
    appUsers: {
        key: "appUsers",
        header: "App users",
        cell: (org: OrgRow) => {
            const appUsers = org.appUserCount ?? 0;
            const members = org.memberCount ?? 0;
            if (members === 0)
                return (
                    <Text muted className="text-sm">
                        —
                    </Text>
                );
            return (
                <Text size="sm">
                    {appUsers.toLocaleString()}{" "}
                    <Text as="span" muted size="sm">
                        ({Math.round((appUsers / members) * 100)}%)
                    </Text>
                </Text>
            );
        },
    } satisfies DataTableColumn<OrgRow>,

    status: {
        key: "status",
        header: "Status",
        cell: (org: OrgRow) =>
            org.status ? (
                <StatusBadge variant={STATUS_VARIANT[org.status]}>{STATUS_LABEL[org.status]}</StatusBadge>
            ) : (
                dash
            ),
    } satisfies DataTableColumn<OrgRow>,

    chatEnabled: {
        key: "chatEnabled",
        header: "Chat",
        cell: (org: OrgRow) => (
            <StatusBadge dot variant={org.chatEnabled ? "success" : "muted"}>
                {org.chatEnabled ? "On" : "Off"}
            </StatusBadge>
        ),
    } satisfies DataTableColumn<OrgRow>,
} as const;

export { DataTable as OrgTable };
